/**
 * План сделки индикатора: вход, стоп, цели, сопровождение.
 *
 * Индикатор отвечает «когда», план — «как»: где поставить стоп, куда
 * целиться, когда перенести в безубыток. Один план на любой индикатор —
 * собранный из блоков, взятый из библиотеки, найденный по отметкам или
 * написанный кодом. По нему студия считает сделки на истории и
 * статистику, график рисует зоны стопа и целей, бот торгует тем же.
 *
 * Все расстояния — в понятных единицах: проценты от цены, средние ходы
 * свечи (ATR) или R — размер стопа. «Цель 2R» значит «прибыль вдвое
 * больше риска», и это одинаково читается на биткойне и на монете за цент.
 */
/** Классика: стоп за свингом, цели 1R / 2R / 3R по трети, безубыток после первой. */
export const DEFAULT_TRADE_PLAN = {
    entry: 'close',
    retestDepth: 50,
    retestBars: 10,
    stop: 'swing',
    stopValue: 0.3,
    maxStopAtr: 4,
    atrPeriod: 14,
    targets: [
        { r: 1, share: 33 },
        { r: 2, share: 33 },
        { r: 3, share: 34 },
    ],
    breakevenAfter: 1,
    trail: null,
    oneAtATime: true,
    maxBars: 0,
    riskMoney: 100,
};
const clamp = (value, min, max, fallback) => typeof value === 'number' && Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : fallback;
/**
 * План из профиля или от человека — в границах, которые имеют смысл.
 * Битые и чужие поля не роняют расчёт: на их месте — значения по умолчанию.
 */
export function normalizeTradePlan(value) {
    if (typeof value !== 'object' || value === null)
        return null;
    const raw = value;
    const d = DEFAULT_TRADE_PLAN;
    const targets = Array.isArray(raw.targets)
        ? raw.targets
            .filter((entry) => typeof entry === 'object' && entry !== null)
            .map((entry) => ({ r: clamp(entry.r, 0.1, 50, 1), share: clamp(entry.share, 0, 100, 0) }))
            .filter((entry) => entry.share > 0)
            .slice(0, 5)
        : d.targets;
    const trailRaw = raw.trail;
    const trailMode = trailRaw && ['atr', 'percent', 'r', 'swing'].includes(String(trailRaw.mode)) ? trailRaw.mode : null;
    return {
        entry: raw.entry === 'retest' ? 'retest' : 'close',
        retestDepth: clamp(raw.retestDepth, 5, 95, d.retestDepth),
        retestBars: Math.round(clamp(raw.retestBars, 1, 200, d.retestBars)),
        stop: raw.stop === 'percent' || raw.stop === 'atr' || raw.stop === 'swing' || raw.stop === 'candle' ? raw.stop : d.stop,
        stopValue: clamp(raw.stopValue, 0, 100, d.stopValue),
        maxStopAtr: clamp(raw.maxStopAtr, 0.5, 50, d.maxStopAtr),
        atrPeriod: Math.round(clamp(raw.atrPeriod, 2, 200, d.atrPeriod)),
        targets,
        breakevenAfter: Math.round(clamp(raw.breakevenAfter, 0, 5, d.breakevenAfter)),
        trail: trailMode && trailRaw ? { mode: trailMode, value: clamp(trailRaw.value, 0.01, 100, 2), afterProfit: trailRaw.afterProfit !== false } : null,
        oneAtATime: raw.oneAtATime !== false,
        maxBars: Math.round(clamp(raw.maxBars, 0, 5000, 0)),
        riskMoney: clamp(raw.riskMoney, 1, 1_000_000, d.riskMoney),
    };
}
//# sourceMappingURL=trade-plan.js.map